import { createSelector } from "reselect";
import { selectPermissionRole } from "containers/App/selectors";

/*
 * Selectors for the gated /admin superadmin section.
 *
 * `permission_role` is the source of truth for superadmin access — it is the
 * enum STRING label ("super_admin"), populated from the /api/v2/session
 * response. Before the session resolves it is falsy (undefined or ""), which
 * the AdminPage guard treats as "still loading" rather than "denied" so a
 * super_admin reloading on /admin isn't bounced to /channels mid-fetch.
 */

const SUPER_ADMIN_ROLE = "super_admin";

/*
 * True only when the session has loaded a user whose role is super_admin.
 * Returns false (not undefined) for any non-super_admin role so callers get a
 * deterministic boolean. Distinguish "denied" from "loading" via
 * selectRoleResolved below.
 */
const selectIsSuperAdmin = () =>
  createSelector(
    selectPermissionRole(),
    (role) => role === SUPER_ADMIN_ROLE
  );

/*
 * True once `permission_role` holds any non-falsy value — i.e. the session
 * response has populated the user. Used to gate the redirect: while the role
 * is still falsy we render a loading state instead of redirecting, avoiding
 * the pre-session "" race that would otherwise eject a real super_admin.
 */
const selectRoleResolved = () =>
  createSelector(selectPermissionRole(), (role) => Boolean(role));

export { selectIsSuperAdmin, selectRoleResolved, SUPER_ADMIN_ROLE };
